import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BeautyProductProvider } from '../../../beauty-shared/context/BeautyProductContext';
import { BEAUTY_PRODUCTS } from '../../../beauty-shared/config/saasModules';
import { SkinSessionProvider } from '../../salon/context/SkinSessionContext';
import { PageBackgroundSync } from '../../salon/components/PageBackgroundSync';
import PageFooter from '../../salon/components/PageFooter';
import { getMobileUploadStatus, postMobileUploadImage } from '../utils/looksApi';
import { useLooksPageCopy } from '../hooks/useLooksPageCopy';
import { resolveFranchiseLogoUrl } from '../../shared/franchiseBrand';
import { syncKioskPoweredByFlag } from '../../shared/kioskPoweredBy';
import './LooksMobileUploadPage.css';

const FACING_USER = 'user';
const FACING_ENV = 'environment';

function LooksMobileUploadInner() {
  const { token } = useParams();
  const { title: pageTitle, cta: pageCta } = useLooksPageCopy();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [sessionOk, setSessionOk] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [facingMode, setFacingMode] = useState(FACING_USER);
  const [cameraError, setCameraError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [preview, setPreview] = useState('');
  const [brandLogo, setBrandLogo] = useState('');
  const [brandName, setBrandName] = useState('');
  const [logoFailed, setLogoFailed] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(
    async (facing) => {
      stopStream();
      setCameraError('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (_) {
        setCameraError('Camera blocked — use Gallery instead.');
      }
    },
    [stopStream],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setSessionError('Invalid link');
        return;
      }
      try {
        const info = await getMobileUploadStatus(token);
        if (cancelled) return;
        const brand = info.branding || {};
        const logo = resolveFranchiseLogoUrl(brand.logo || '');
        const name = String(brand.name || '').trim();
        if (logo) setBrandLogo(logo);
        if (name) setBrandName(name);
        if (typeof brand.powered_by === 'boolean') {
          syncKioskPoweredByFlag(brand.powered_by);
        }
        if (info.status === 'expired' || info.status === 'consumed') {
          setSessionError(info.status === 'consumed' ? 'Already used.' : 'Link expired.');
          return;
        }
        setSessionOk(true);
      } catch (err) {
        if (!cancelled) setSessionError(err.message || 'Link unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!sessionOk || sent) return undefined;
    void startCamera(facingMode);
    return () => stopStream();
  }, [sessionOk, sent, facingMode, startCamera, stopStream]);

  const flipCamera = () => {
    setFacingMode((prev) => (prev === FACING_USER ? FACING_ENV : FACING_USER));
  };

  const captureFrame = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setCameraError('Camera not ready');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === FACING_USER) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    setPreview(canvas.toDataURL('image/jpeg', 0.92));
    stopStream();
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(String(reader.result || ''));
      stopStream();
    };
    reader.readAsDataURL(file);
  };

  const sendToKiosk = async () => {
    if (!preview || !token) return;
    setBusy(true);
    setCameraError('');
    try {
      await postMobileUploadImage(token, preview);
      setSent(true);
      stopStream();
    } catch (err) {
      setCameraError(err.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    setPreview('');
    setSent(false);
    void startCamera(facingMode);
  };

  const showLogo = Boolean(brandLogo) && !logoFailed;
  const shortTitle = String(pageTitle || 'Makeup').trim() || 'Makeup';

  const brandBar = (
    <header className="looks-mobile-upload__brand">
      {showLogo ? (
        <img
          src={brandLogo}
          alt={brandName || 'Franchise'}
          className="looks-mobile-upload__logo"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="looks-mobile-upload__brand-fallback">{brandName || shortTitle}</span>
      )}
      <h1 className="looks-mobile-upload__title">{shortTitle}</h1>
    </header>
  );

  const footer = (
    <div className="looks-mobile-upload__footer">
      <PageFooter />
    </div>
  );

  if (sessionError) {
    return (
      <div className="looks-mobile-upload looks-mobile-upload--status">
        {brandBar}
        <div className="looks-mobile-upload__card">
          <h2>Unavailable</h2>
          <p>{sessionError}</p>
        </div>
        {footer}
      </div>
    );
  }

  if (!sessionOk) {
    return (
      <div className="looks-mobile-upload looks-mobile-upload--status">
        {brandBar}
        <div className="looks-mobile-upload__card">
          <p>Opening…</p>
        </div>
        {footer}
      </div>
    );
  }

  if (sent) {
    return (
      <div className="looks-mobile-upload looks-mobile-upload--status">
        {brandBar}
        <div className="looks-mobile-upload__card looks-mobile-upload__card--ok">
          <h2>Sent</h2>
          <p>You can close this page.</p>
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div className="looks-mobile-upload">
      {brandBar}

      <div className="looks-mobile-upload__stage">
        {preview ? (
          <img src={preview} alt="Selected" className="looks-mobile-upload__preview" />
        ) : (
          <video
            ref={videoRef}
            className={`looks-mobile-upload__video${facingMode === FACING_USER ? ' is-mirror' : ''}`}
            playsInline
            muted
            autoPlay
          />
        )}
      </div>

      {cameraError ? <p className="looks-mobile-upload__error">{cameraError}</p> : null}

      <div className={`looks-mobile-upload__actions${preview ? ' is-confirm' : ''}`}>
        {!preview ? (
          <>
            <button type="button" className="looks-mobile-upload__btn" onClick={flipCamera}>
              Flip
            </button>
            <button
              type="button"
              className="looks-mobile-upload__btn looks-mobile-upload__btn--primary"
              onClick={captureFrame}
            >
              Capture
            </button>
            <button
              type="button"
              className="looks-mobile-upload__btn"
              onClick={() => fileInputRef.current?.click()}
            >
              Gallery
            </button>
          </>
        ) : (
          <>
            <button type="button" className="looks-mobile-upload__btn" onClick={retake} disabled={busy}>
              Retake
            </button>
            <button
              type="button"
              className="looks-mobile-upload__btn looks-mobile-upload__btn--primary"
              onClick={() => void sendToKiosk()}
              disabled={busy}
            >
              {busy ? 'Sending…' : pageCta}
            </button>
          </>
        )}
      </div>

      {footer}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="looks-mobile-upload__file"
        onChange={onFile}
      />
    </div>
  );
}

export default function LooksMobileUploadPage() {
  return (
    <BeautyProductProvider product={BEAUTY_PRODUCTS.looks} naturals>
      <SkinSessionProvider>
        <PageBackgroundSync />
        <LooksMobileUploadInner />
      </SkinSessionProvider>
    </BeautyProductProvider>
  );
}
